import axios from 'axios'
import { useAuthActions, useAuthState } from './AuthContext'

const API_BASE_URL = import.meta.env.VITE_API_BASE_DEV_URL

const tokenVaultApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

tokenVaultApi.interceptors.request.use(
  request => {
    return request
  },
  error => {
    if (error.status === 401) console.log('Unauthorized access:', error.status)
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

tokenVaultApi.interceptors.response.use(
  response => {
    if (response.status === 200)
      console.log('Successful read:', response.status)
    if (response.status === 201)
      console.log('Credential created:', response.status)
    if (response.status === 204)
      console.log('Credential deleted:', response.status)
    return response
  },
  error => {
    if (error.status === 400) console.log('Invalid JSON:', error.status)
    if (error.status === 401)
      console.log('Problem with token, reconnect through PKCE:', error.status)
    if (error.status === 403)
      console.log('Missing scope / quote reached', error.status)
    if (error.status === 404) console.log('Credential not found:', error.status)
    if (error.status === 409)
      console.log('Duplicate write conflict:', error.status)
    if (error.status === 412)
      console.log('If-Match version is stale:', error.status)
    if (error.status === 413)
      console.log('Payload size too large:', error.status)
    if (error.status === 429) console.log('API throttling:', error.status)
    console.error('Response error:', error.response)
    return Promise.reject(error)
  }
)

// --- useApiClient Hook ---
export function useApiClient () {
  const { accessToken } = useAuthState()
  const { logout } = useAuthActions()

  const getWithAuth = async (url, config = {}) => {
    try {
      const response = await tokenVaultApi.get(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined
        }
      })
      return response.data
    } catch (error) {
      if (error.response?.status === 401) logout()
      throw error
    }
  }

  const postWithAuth = async (url, data, config = {}) => {
    try {
      const response = await tokenVaultApi.post(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined
        }
      })
      return response.data
    } catch (error) {
      if (error.response?.status === 401) logout()
      throw error
    }
  }

  const patchWithAuth = async (url, data, etag, config = {}) => {
    // Empty strings and presence of "api_name" in the body are rejected
    try {
      const response = await tokenVaultApi.patch(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          'If-Match': etag
        }
      })
      return response
    } catch (error) {
      throw error
    }
  }

  const putWithAuth = async (url, data, etag, config = {}) => {
    // Replaces base_url(?) and the complete set of secret fields, requires
    // the most recent ETag with If-Match
    try {
      const response = await tokenVaultApi.put(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          'If-Match': etag
        }
      })
      return response.data
    } catch (error) {
      if (error.response?.status === 404) console.error('Missing credentials')
      throw error
    }
  }

  const deleteWithAuth = async (url, config = {}) => {
    try {
      const response = await tokenVaultApi.delete(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined
        }
      })
      return response.data
    } catch (error) {
      if (error.response?.status === 404) console.error('Missing credentials')
      throw error
    }
  }

  return { getWithAuth, postWithAuth, patchWithAuth, deleteWithAuth }
}
